import Button from '@/components/UI/Button';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import Header from '@/components/UI/Header';
import { StudyLookup } from '@/components/UI/StudyLookup/StudyLookup';
import type { components } from '@/generated/internal-api/openapi-types';
import { _isNotBlank } from '@/littledash';
import type { StudyInfoV1 } from '@/model/Study.model';
import { AgentSessionService } from '@/support/AgentSessionService';
import { useAgentSession } from '@/support/Hooks/agent/useAgentSession';
import type { FC } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form@latest';
import { InVivoPulseResponse } from './InVivoPulseResponse';
import styles from './InVivoPulse.module.scss';

type StudyApiId = components['schemas']['StudyApiId.schema'];

type AgentVariant = '' | 'benchling_ask' | 'benchling_deep_research';

interface PulseFormValues {
  study_api_id: StudyApiId | '';
  user_prompt: string;
  agent: AgentVariant;
}

const agentOptions: ReadonlyArray<{ label: string; value: AgentVariant; disabled?: boolean }> = [
  { label: 'Pulse only', value: '' },
  { label: 'Benchling Ask (delegated auth required)', value: 'benchling_ask', disabled: true },
  { label: 'Benchling Deep Research (delegated auth required)', value: 'benchling_deep_research', disabled: true },
];

/**
 * Admin-console screen that exercises the Pulse agent end-to-end:
 * picks a study, kicks off a session, and renders the streaming response
 * as it arrives over SSE.
 */
export const InVivoPulse: FC = () => {
  const formMethods = useForm<PulseFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      study_api_id: '',
      user_prompt: '',
      agent: '',
    },
  });

  const { state, createSession, cancel, reset } = useAgentSession();
  const { phase } = state;

  const isBusy = phase === 'creating' || phase === 'streaming';
  const studyApiId = useWatch({ control: formMethods.control, name: 'study_api_id' });
  const submitDisabled = isBusy || studyApiId === '';

  const handleSubmit = async (values: PulseFormValues) => {
    const studyApiIdValue = values.study_api_id;
    if (studyApiIdValue === '') {
      return;
    }
    await createSession((signal) =>
      AgentSessionService.createPulseSession(
        {
          study_api_id: studyApiIdValue,
          user_prompt: _isNotBlank(values.user_prompt) ? values.user_prompt : null,
          agent: values.agent === '' ? null : values.agent,
        },
        signal
      )
    );
  };

  const handleReset = () => {
    reset();
    formMethods.reset();
  };

  return (
    <div className="pb3" data-test-component="InVivoPulse" data-test-element="container" data-testid="invivo-pulse">
      <Header mainHeaderText="InVivo Pulse" subHeaderText="Ask Pulse a question about a study" />
      <div className={`${styles.layout} pt4`}>
        <div className="ui-card pa4 flex flex-column w-50" data-test-element="form">
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
              <Controller
                control={formMethods.control}
                name="study_api_id"
                rules={{ required: 'Study is required' }}
                render={({ field }) => (
                  <FormField name="study_api_id" label="Study" required testId="invivo-pulse__study">
                    <StudyLookup
                      onChange={(study?: StudyInfoV1) => {
                        field.onChange(study?.api_id ?? '');
                      }}
                      isDisabled={isBusy}
                      placeholder="Search studies"
                    />
                  </FormField>
                )}
              />
              <div className="mt3">
                <Controller
                  control={formMethods.control}
                  name="user_prompt"
                  render={({ field }) => (
                    <FormField name="user_prompt" label="Prompt" testId="invivo-pulse__prompt">
                      <textarea
                        {...field}
                        rows={4}
                        placeholder="e.g. Why is animal X trending down?"
                        disabled={isBusy}
                        aria-label="Pulse prompt"
                        data-testid="invivo-pulse__prompt-input"
                      />
                    </FormField>
                  )}
                />
              </div>
              <div className="mt3">
                <Controller
                  control={formMethods.control}
                  name="agent"
                  render={({ field }) => (
                    <FormField name="agent" label="Agent" testId="invivo-pulse__agent">
                      <select
                        {...field}
                        disabled={isBusy}
                        aria-label="Pulse agent variant"
                        data-testid="invivo-pulse__agent-select"
                      >
                        {agentOptions.map((option) => (
                          <option key={option.value} value={option.value} disabled={option.disabled}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  )}
                />
              </div>
              <div className={`${styles.actions} mt4`} data-test-element="actions">
                <Button
                  submit
                  primary
                  disabled={submitDisabled}
                  loading={phase === 'creating'}
                  loadingText="Starting"
                  testId="invivo-pulse__submit"
                >
                  Ask Pulse
                </Button>
                {phase === 'streaming' && (
                  <Button outline onClick={cancel} testId="invivo-pulse__cancel">
                    Cancel
                  </Button>
                )}
                {(phase === 'completed' || phase === 'failed' || phase === 'cancelled') && (
                  <Button plain onClick={handleReset} testId="invivo-pulse__reset">
                    New question
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </div>
        <div className="w-50" data-test-element="response">
          <InVivoPulseResponse
            phase={phase}
            streamEvents={state.streamEvents}
            session={state.session}
            error={state.error}
          />
        </div>
      </div>
    </div>
  );
};
